import { useEffect, useState } from 'react';

/**
 * Admin allowlist (client-side hint only — the server is the real gate via
 * ADMIN_EMAILS env on the Pages Function side). Used by Profile to
 * conditionally render the "Admin" link, and by /admin to short-circuit the
 * lookup UI when a non-admin lands on the route via direct URL.
 *
 * Stored as SHA-256 of the lowercased address, not the address itself.
 * This is a public JS bundle: anyone who opened devtools could read the
 * plaintext owner email straight out of it and hand it to a scraper. Hashing
 * is not access control — the Pages Function still re-checks every request
 * against its own env allowlist — it just stops the bundle being a free
 * address book.
 *
 * To add an admin:
 *   1. add the address to ADMIN_EMAILS in Cloudflare Pages settings (the
 *      real gate), and
 *   2. add its digest here:
 *      node -e "console.log(require('crypto').createHash('sha256').update('them@example.com').digest('hex'))"
 *
 * If the two drift, the link shows (or hides) for the wrong user — cosmetic
 * — and the server still returns 403.
 */
const ADMIN_EMAIL_HASHES: ReadonlySet<string> = new Set([
  // owner
  '7cf8f7c89556648608f83c3c32071811ab053e7e8974ee3799a16d9e39d553c6',
]);

async function sha256Hex(input: string): Promise<string | null> {
  // crypto.subtle only exists in a secure context. Everything this gates is
  // cosmetic, so a missing digest degrades to "not an admin" rather than
  // throwing in the middle of a render.
  if (typeof crypto === 'undefined' || !crypto.subtle) return null;
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Async because Web Crypto is async. Resolves false for null/unknown emails. */
export async function isAdminEmail(email: string | null | undefined): Promise<boolean> {
  if (!email) return false;
  const hash = await sha256Hex(email.toLowerCase());
  return hash !== null && ADMIN_EMAIL_HASHES.has(hash);
}

/**
 * Render-friendly wrapper: starts false, flips true once the digest resolves.
 *
 * Starting false is the safe default — an admin sees the Admin link appear a
 * frame late, whereas the opposite would flash admin UI at everyone.
 */
export function useIsAdminEmail(email: string | null | undefined): boolean {
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!email) {
      setIsAdmin(false);
      return;
    }
    void isAdminEmail(email).then((result) => {
      if (!cancelled) setIsAdmin(result);
    });
    return () => {
      cancelled = true;
    };
  }, [email]);

  return isAdmin;
}

/** Hard-coded fallback matching ACCOUNTS.md § Domain. Override locally
 *  by setting VITE_DOMAIN_EXPIRES_AT in apps/app/.env.local. */
export const DOMAIN_EXPIRY_FALLBACK = '2027-04-18';
